<template>
  <div ref="reviewContainer">
    <Divider margin-top="var(--space-10)" margin-bottom="var(--space-10)" />
    <NewProductReviewSummary
      :loading="loading"
      :error="error"
      :product="product"
      :reviewUrl="reviewUrl"
      :filter-star="filters.rating"
      :filter-tag="filters.tag"
      :available-tags="enableSimilar ? [] : availableTags"
      :review-count="countExactProduct || (product && product.review ? product.review.count : 0)"
      @updateStars="updateStarsFilter"
      @updateTags="updateTagsFilter"
      @ratingClicked="showMoreReviews"
    ></NewProductReviewSummary>

    <div class="flex-grid-review">
      <template v-if="reviewsExactProduct && product">
        <NewProductReview
          v-for="review in reviewsExactProduct.slice(0, limitReviewCount())"
          :key="review.id"
          :review="review"
          :product-title="product.title"
          :show-product="false"
          :show-featured="true"
          :show-read-more="show"
        ></NewProductReview>
      </template>
    </div>
    <template v-if="(!countExactProduct || !count) && (!loading || reviews.length)">
      <div v-if="product && product.review && !product.review.count" class="no-product-reviews">
        <div v-if="isNewProduct">
          This newer product is still making itself at home,<br/>
          so you might get to write the first review. (Congrats!)
        </div>
        <template v-else>
          <div>
            Hmm, there aren’t any reviews yet<template v-if="product"> for the {{ product.title }}</template> -
          </div>
          <div>which means you might get to write the first one. (Congrats!)</div>
        </template>
      </div>
      <div v-else-if="error" class="no-product-reviews">
        <div>Hmm, something went wrong while loading reviews. Please try again later.</div>
      </div>
      <div v-else class="no-product-reviews">No reviews match the selected filters.</div>
    </template>
    <template v-if="product && reviewsSimilarProduct && reviewsSimilarProduct.length">
      <div class="similar-product-review-container">
        <div class="similar-reviews-title">Reviews for similar products</div>
        <Divider margin-top="var(--space-2)" margin-bottom="var(--space-6)" height="2px" />
        <div class="flex-grid-review">
          <NewProductReview
            v-for="review in reviewsSimilarProduct.slice(0, limitReviewCount())"
            :key="review.id"
            :review="review"
            :show-product="true"
            :show-featured="true"
            :show-read-more="show"
          ></NewProductReview>
        </div>
      </div>
    </template>
    <template v-if="reviewsSimilarProduct.length !== 0 || reviewsExactProduct.length !== 0" class="read-more-reviews-button">
      <div v-if="loading" class="loader tiny"></div>
      <div class="review-button">
        <button class="outlined coral read-review-button" @click="showMoreReviews" aria-label="Open more customer reviews list">Read More Reviews</button>
      </div>
    </template>
    <NewModalReview
      v-if="showMore"
      :product="product"
      :loading="loading"
      :error="error"
      :isNewProduct="isNewProduct"
      :reviewUrl="reviewUrl"
      :filter-star="filters.rating"
      :filter-tag="filters.tag"
      :available-tags="enableSimilar ? [] : availableTags"
      :review-count="countExactProduct || (product && product.review ? product.review.count : 0)"
      :enable-similar="enableSimilar"
      :reviews-exact-product="reviewsExactProduct"
      :reviews-similar-product="reviewsSimilarProduct"
      :count-exact-product="countExactProduct"
      :reviews="reviews"
      :count="count"
      :show="show"
      :show-actions="showActions"
      :is-more="isMore"
      @updateStars="updateStarsFilter"
      @updateTags="updateTagsFilter"
      @close="goBack()"
      @ratingClicked="showMoreReviews"
      @loadMore="loadMore"
    ></NewModalReview>
  </div>
</template>

<script>
import { onMounted, ref } from 'vue';
import { Divider } from '@/pixel/index';
import ProductReviewSection from './ProductReviewSection.vue';
import NewProductReview from './NewProductReview.vue';
import NewProductReviewSummary from './NewProductReviewSummary.vue';
import useProductReviews from '@/compositions/useProductReviews.js';
import Tracking from '@/components/commons/Tracking';
import NewModalReview from '@/components/reviews/NewModalReview.vue';
import Chat from '@/components/commons/Chat';
import { watch } from '@vue/runtime-core';

export default {
  name: 'NewProductReviews',
  components: {
    NewModalReview,
    NewProductReview,
    NewProductReviewSummary,
    Divider,
    ProductReviewSection,
  },
  props: {
    product: {
      type: Object,
      default: () => ({}),
    },
    show: {
      type: Boolean,
      default: true,
    },
    reviewUrl: {
      type: String,
      default: null,
    },
  },

  computed: {
    reviewsExactProduct() {
      return (this.reviews || []).filter((review) => this.isProductExactMatchReview(this.filters.product, review));
    },

    reviewsSimilarProduct() {
      return (this.reviews || []).filter((review) => !this.isProductExactMatchReview(this.filters.product, review));
    },

    enableSimilar() {
      const SIMILAR_REVIEW_THRESHOLD = 5;
      return this.countExactProduct < SIMILAR_REVIEW_THRESHOLD;
    },
  },

  async setup(props, { emit }) {
    const {
      filters,
      availableTags,
      count,
      countExactProduct,
      page,
      maxPage,
      reviews,
      loading,
      error,
      isMore,
      load,
      loadMore,
      updateTagsFilter,
      updateStarsFilter,
    } = useProductReviews();

    const showMore = ref(false);
    const showActions = ref(false);
    const hideProductHeader = ref(false);
    const readMore = ref(false);
    const showReadBtn = ref(false);
    const showReadBtnMobile = ref(false);
    const horizontalCoordinates = ref(0);
    const verticalCoordinates = ref(0);
    const reviewContainer = ref(null);

    let intersectionTimeout = null;

    function checkEngagement() {
      if ('IntersectionObserver' in window && reviewContainer.value) {
        const observer = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (entry.intersectionRatio > 0.5) {
                intersectionTimeout = setTimeout(() => {
                  emit('reviewsEngaged');
                  observer.unobserve(reviewContainer.value);
                }, 5000);
              } else {
                clearTimeout(intersectionTimeout);
              }
            });
          },
          {
            threshold: 0.5,
          },
        );

        observer.observe(reviewContainer.value);
      }
    }

    onMounted(() => {
      checkEngagement();
    });

    watch(() => props?.product, async () => {
      await load(props?.product?.hashKey);
    });

    await load(props?.product?.hashKey);

    return {
      isNewProduct: props.product?.isNewProduct,
      filters,
      availableTags,
      count,
      countExactProduct,
      page,
      maxPage,
      reviews,
      loading,
      error,
      isMore,
      load,
      loadMore,
      updateTagsFilter,
      updateStarsFilter,
      showMore,
      showActions,
      hideProductHeader,
      readMore,
      showReadBtn,
      showReadBtnMobile,
      horizontalCoordinates,
      verticalCoordinates,
      reviewContainer,
    };
  },

  methods: {
    isProductExactMatchReview(hashKey, review) {
      return !![...(review.alternativeHashKeys || []), review.activeVersionHashKey, review.productHashKey].find((hk) => hk === hashKey);
    },
    showMoreReviews() {
      this.horizontalCoordinates = window.scrollX;
      this.verticalCoordinates = window.scrollY;
      this.showActions = true;
      this.showMore = true;
      const html = document.querySelector('html');
      html?.classList.add('overflow-hidden');
      Chat.hideChatWindow();
      Tracking.track('PDP_Click_More_Review');
    },
    goBack() {
      this.showMore = false;
      this.updateStarsFilter('');
      const html = document.querySelector('html');
      html?.classList.remove('overflow-hidden');
      Chat.defaultChatWindow();
      window.scrollTo(this.horizontalCoordinates, this.verticalCoordinates);
    },
    limitReviewCount() {
      if (!this.readMore) {
        return 3;
      } else {
        return this.reviews.length;
      }
    },
  },
};
</script>

<style lang="postcss">
@import '@/pixel/style/variables';
@import '@/pixel/style/mixins';

.product-reviews-block {
  .similar-reviews-title {
    font-size: var(--font-size-16);
    line-height: 150%;
    align-items: center;
    margin-top: var(--space-10);
    margin-bottom: var(--space-2);
    color: var(--grey-medium);
  }
}

.no-product-reviews {
  margin-bottom: var(--space-6);
}

.flex-grid-review {
  display: flex;
  flex-wrap: nowrap;
  grid-gap: var(--space-10);
  margin-top: var(--space-6);
  @media (max-width: calc($tablet-breakpoint - 1px)) {
    flex-wrap: wrap;
    display: block;
  }
}

::v-deep .similar-product-review-container {
  margin-top: var(--space-10);
  margin-bottom: var(--space-10);
}

::v-deep .review-button {
  margin-bottom: var(--space-10);
}

.read-review-button {
  height: var(--space-10);
  @media (min-width: $extra-small-breakpoint) and (max-width: calc($tablet-breakpoint - 1px)) {
    width: calc(100vw - 40px);
    height: var(--space-10);
  }
}
</style>
